import nacl from 'tweetnacl';
import { sha512 } from '@noble/hashes/sha512';
import { sha256 } from '@noble/hashes/sha256';

/**
 * Decrypts a ciphertext encrypted with a domain-separated XSalsa20-Poly1305 key.
 *
 * This function:
 * - Derives the nonce deterministically from the sender's ephemeral public key.
 * - Derives a layer-specific decryption key using SHA-512(sharedSecret || label).
 * - Attempts authenticated decryption using XSalsa20-Poly1305 (`nacl.secretbox.open`).
 *
 * If authentication fails (e.g., wrong key, wrong nonce, tampering), it returns `null`.
 *
 * @param ciphertext           The encrypted message to decrypt (includes Poly1305 tag).
 * @param ephemeralPublicKey   The sender's 32-byte ephemeral Curve25519 public key.
 * @param sharedSecret         A 32-byte shared secret (e.g., from ECDH or hybrid).
 * @param layer                Numeric layer index, used for domain-separated key derivation.
 * @returns The decrypted message (`Uint8Array`), or `null` if decryption/authentication fails.
 */
export function decryptLayer(
  ciphertext: Uint8Array,
  ephemeralPublicKey: Uint8Array,
  sharedSecret: Uint8Array,
  layer: number
): Uint8Array | null {
  // === Input validation ===
  if (ephemeralPublicKey.length !== 32) {
    throw new Error('ephemeralPublicKey must be 32 bytes');
  }
  if (sharedSecret.length !== 32 && sharedSecret.length !== 64) {
    throw new Error('sharedSecret must be 32 or 64 bytes (hybrid input)');
  }

  // === Derive the nonce from ephemeral public key ===
  // nonce = SHA-256(ephemeralPublicKey)[0..24]
  const nonceBytes = sha256(ephemeralPublicKey);
  const nonce = new Uint8Array(nonceBytes.slice(0, nacl.secretbox.nonceLength));

  // === Build domain-separated label ===
  const label = new TextEncoder().encode(`unlink-encryption-layer${layer}`);

  // === Prepare input for key derivation: sharedSecret || label ===
  const input = new Uint8Array(sharedSecret.length + label.length);
  input.set(sharedSecret);
  input.set(label, sharedSecret.length);

  // === Derive the decryption key using SHA-512 and truncate to 32 bytes ===
  const derivedKey = sha512(input).slice(0, 32);

  // === Attempt to decrypt using derived key and deterministic nonce ===
  const plaintext = nacl.secretbox.open(ciphertext, nonce, derivedKey);

  return plaintext || null;
}